import { memo } from "react";
import { MinerIdFieldPropsI } from "./MinerIdField.props";
import cn from "classnames";
import styles from "./MinerIdField.module.scss";
import Icon from "@components/global/new/Icon/Icon";
import ColoredChanges from "../ColoredChanges/ColoredChanges";
import { useMinerIdFieldControl } from "./useMinerIdFieldControl";
import Copy from "@components/global/new/Copy/Copy";
import { Button, Tooltip } from "antd";
import { useIntl } from "react-intl";
import CopyItem from "../../../../global/new/CopyItem/CopyItem";
import MinerDataCopy from "./MinerDataCopy/MinerDataCopy";

import close from "@assets/icons/close.svg";
import { ExclamationCircleOutlined, RollbackOutlined } from "@ant-design/icons";
import { ReactComponent as WarningIcon } from "@assets/icons/triangle-alert.svg";

function MinerIdField({ ...props }: MinerIdFieldPropsI) {
  const { formatMessage } = useIntl();
  const { text } = props;
  const control = useMinerIdFieldControl(props);

  if (!control.me?.is_admin)
    return (
      <CopyItem
        className={cn(styles.user_minerId, props.className)}
        copyClassName={props.copyClassName}
        text={props.isDeviceID ? "" : text}
      >
        {props.isDeviceID && (
          <ExclamationCircleOutlined
            color="red"
            style={{ fontSize: 20 }}
            rev={undefined}
            className={styles.err_icon}
          />
        )}
        <ColoredChanges className={styles.colored}>
          {props.isDeviceID ? (
            <div className={styles.deviceID}>{control.newId}</div>
          ) : (
            control.newId
          )}
        </ColoredChanges>
      </CopyItem>
    );

  if (!control.isEdit) {
    return (
      <MinerDataCopy
        className={cn(styles.copy_wrapper, props.className)}
        copyClassName={props.copyClassName}
        text={text}
        minerId={props.minerId}
        isDeviceID={props.isDeviceID}
        hideDataCopy={props.hideDataCopy}
      >
        {/* дабы не дублировалась иконка WarningIcon добавлена проверка на !props.isDeviceID */}
        {control.miner.has_errors && !props.isDeviceID && (
          <div className={styles.errors_icon}>
            <WarningIcon />
          </div>
        )}
        {props.isDeviceID && (
          <div className={styles.errors_icon}>
            <ExclamationCircleOutlined
              color="red"
              style={{ fontSize: 20 }}
              rev={undefined}
              className={styles.err_icon}
            />
          </div>
        )}
        <ColoredChanges
          className={styles.colored}
          onClick={props.isDeviceID ? undefined : control.onFocus}
        >
          {props.isDeviceID ? (
            <div className={styles.deviceID}>{control.newId}</div>
          ) : (
            control.newId
          )}
        </ColoredChanges>
        {control.miner.miner_id.includes("_") &&
          control.miner.miner_id.split("_")[0].length === 12 && (
            <Button
              type="text"
              title={formatMessage({
                id: "machines.miner_row.miner_id.restore",
              })}
              onClick={control.restore}
            >
              <RollbackOutlined />
            </Button>
          )}
      </MinerDataCopy>
    );
  }

  return (
    <div className={cn(styles.main_wrapper, props.className)}>
      <div
        ref={control.wrapperRef}
        tabIndex={100}
        className={cn(styles.wrapper)}
        onFocus={props.isDeviceID ? undefined : control.onFocus}
        onBlur={(event) => {
          const nextTarget = event.relatedTarget;

          if (nextTarget && event.currentTarget.contains(nextTarget as Node)) return;

          control.onBlur();
        }}
      >
        <textarea
          rows={control.isEdit ? 2 : control.newId.length >= 10 ? 2 : 1}
          maxLength={30}
          value={control.newId}
          wrap={"soft"}
          ref={control.textAreaRef}
          tabIndex={-1}
          onChange={(e) => control.setNewId(e.target.value.replace(/\s/g, ""))}
          onKeyDown={(e) => control.onKeyDown(e)}
          className={cn({
            [styles.edit]: control.isEdit,
          })}
        />

        <div
          className={cn(styles.icons, {
            [styles.edit]: control.isEdit,
          })}
          tabIndex={100}
        >
          <div
            className={styles.submit}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              control.onClickHandler(e);
            }}
          />
          <Icon
            src={close}
            className={styles.close}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              control.onCancel(e);
            }}
          />
        </div>
      </div>
      <Tooltip
        open={control.hasEqualName}
        placement="right"
        title={
          <p className={styles.error_name}>
            {formatMessage({ id: "machines.miner_row.miner_id.eqaul_id" })}
          </p>
        }
        color="#f44336"
      >
        <Copy
          text={control.newId}
          size={18}
          className={cn(styles.copy, props.copyClassName)}
        />
      </Tooltip>
    </div>
  );
}

export default memo(MinerIdField);
